<script setup lang="ts">
import { z } from 'zod'

import VRadio from '~/components/inputs/radio.vue'
import HeightExpand from '~/components/transitions/heightExpand.vue'
import { Sm500Error } from '~/components/typography'

import { useFieldValidation } from '~/composables/useFieldValidation'
import { useSortTagsByTranslatedName } from '~/composables/useTagHelpers'

import { useTagStore } from '~/stores/tag'

import type TagTypeKey from '~/types/tagTypeKey'

type Props = {
  typeName: TagTypeKey
  modelValue: number[]
  readonly?: boolean
  pValidity?: boolean
}
type Emits = {
  'update:modelValue': [value: number[]]
  change: []
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  pValidity: true,
})
const emit = defineEmits<Emits>()
const { t } = useI18n()
const _uid = useId()

const sortTagsByTranslatedName = useSortTagsByTranslatedName()
const { GET_TAG_TYPE_FROM_NAME, GET_TAGS_FROM_IDS } = useTagStore()

const { value: radioFieldValue, errorMessage: radioFieldErrorMessage } =
  useFieldValidation<number[]>(
    'singleRadioSelect',
    z
      .number()
      .array()
      .nonempty({
        message: t('tags.toggleAllAndType.errors.radioNotSelected'),
      }),
    {
      syncVModel: true,
      validateOnValueUpdate: false,
      initialValue: props.modelValue,
    },
  )

const tagType = computed(() => {
  return GET_TAG_TYPE_FROM_NAME(props.typeName)
})

const availableTags = computed(() => {
  return GET_TAGS_FROM_IDS(tagType.value?.tag_ids ?? [])
    .sort((a, b) => sortTagsByTranslatedName(props.typeName, a, b))
    .sort((tag) => {
      return tag.name.includes('_') ? -1 : 1
    })
})
</script>

<template>
  <div :id="_uid" class="singleRadioSelect tw-grid tw-grid-cols-1 tw-gap-4">
    <VRadio
      v-for="tag in availableTags"
      :key="tag.id"
      :model-value="radioFieldValue"
      :value-name="tag.id"
      :label="$t(`tags.track_age.${tag.name}`)"
      :p-validity="pValidity"
      @update:model-value="emit('update:modelValue', $event)"
      @change="emit('change')"
    />
    <HeightExpand>
      <Sm500Error v-if="!pValidity">{{
        t('tags.toggleAllAndType.errors.radioNotSelected')
      }}</Sm500Error>
    </HeightExpand>
  </div>
</template>
